import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { AuthCredential } from 'firebase/auth'
import { useAuth } from './AuthProvider'
import { PendingGoogleLinkError } from './AuthProvider'

export function SignInScreen() {
  const { signIn, signInWithGoogle, completePendingGoogleLink } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Set when Google sign-in returns auth/account-exists-with-different-credential.
  // While set, the form switches to a "confirm password to link Google" prompt.
  const [pendingLink, setPendingLink] = useState<{
    email: string
    credential: AuthCredential
  } | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (pendingLink) {
        await completePendingGoogleLink(pendingLink.email, password, pendingLink.credential)
        setPendingLink(null)
      } else {
        await signIn(email.trim(), password)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  const handleGoogle = async () => {
    setBusy(true)
    setError(null)
    try {
      await signInWithGoogle()
    } catch (err: unknown) {
      if (err instanceof PendingGoogleLinkError) {
        setPendingLink({ email: err.email, credential: err.pendingCredential })
        setEmail(err.email)
        setPassword('')
        setError(err.message)
        return
      }
      const message = err instanceof Error ? err.message : 'Google sign-in failed'
      // Silence user-initiated popup cancellations — these are not errors.
      if (!message.includes('popup-closed-by-user') && !message.includes('cancelled-popup-request')) {
        setError(message)
      }
    } finally {
      setBusy(false)
    }
  }

  const cancelLink = () => {
    setPendingLink(null)
    setError(null)
    setPassword('')
  }

  return (
    <div className="min-h-dvh flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="text-sm text-text-tertiary hover:text-text-secondary mb-6 inline-block"
        >
          ← Back
        </Link>
        <h1 className="text-3xl font-semibold mb-2">Govu</h1>
        <p className="text-text-secondary mb-8">
          {pendingLink
            ? 'Confirm your password to link your Google account.'
            : 'Sign in to your library.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={!!pendingLink}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-md text-text-primary focus:outline-none focus:border-accent-primary disabled:opacity-60 read-only:opacity-60"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-md text-text-primary focus:outline-none focus:border-accent-primary"
            />
          </div>

          {error && (
            <p className="text-sm text-red-400 break-words">{error}</p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full py-2.5 bg-accent-primary hover:bg-accent-hover text-white rounded-md font-medium disabled:opacity-50"
          >
            {busy
              ? pendingLink
                ? 'Linking…'
                : 'Signing in…'
              : pendingLink
                ? 'Link Google account'
                : 'Sign in'}
          </button>

          {pendingLink && (
            <button
              type="button"
              onClick={cancelLink}
              disabled={busy}
              className="w-full py-2 text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </form>

        {!pendingLink && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border-primary" />
              <span className="text-xs text-text-tertiary">or</span>
              <div className="flex-1 h-px bg-border-primary" />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className="w-full py-2.5 border border-border-primary hover:border-text-secondary text-text-primary rounded-md font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.3 29.4 35.5 24 35.5c-6.3 0-11.5-5.2-11.5-11.5S17.7 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.9l5.7-5.7C33.6 6.3 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.8 0 19.5-8.7 19.5-19.5 0-1.2-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.9l5.7-5.7C33.6 6.3 29.1 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13-5l-6-5.1c-2 1.4-4.4 2.2-7 2.2-5.4 0-9.9-3.4-11.5-8.3l-6.6 5.1C9.6 39.1 16.2 43.5 24 43.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.4l6 5.1c-.4.4 6.4-4.6 6.4-14 0-1.2-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-xs text-text-tertiary mt-6 text-center">
              Have an invite code?{' '}
              <Link to="/register" className="text-accent-primary hover:underline">
                Create an account
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
